import { useAuth as useAuthDS, useAuthAxios } from '@artprov/design-system';
import { useEffect, useRef } from 'react';
import { useUserDetailsStore } from '../zustand/userDetails.store.js';
import * as sentry from "@sentry/browser";

/**
 * Hook personalizado que extiende useAuth del Design System con userDetails del backend
 * 
 * Implementa un sistema de caché global usando Zustand para evitar múltiples llamadas
 * a /usuario/{username} cuando varios componentes usan este hook.
 * 
 * @returns {Object} Estado de autenticación extendido
 */
export const useComprasAuth = () => {
  const auth = useAuthDS();
  const api = useAuthAxios('compras-back');
  
  // Seleccionar solo los datos del store (no las funciones)
  const userDetails = useUserDetailsStore((state) => state.userDetails);
  const loading = useUserDetailsStore((state) => state.loading);
  //const lastUsername = useUserDetailsStore((state) => state.lastUsername);

  useEffect(() => {
    // Si no está autenticado o no hay usuario, limpiar detalles
    if (!auth.authenticated || !auth.user?.preferred_username) {
      const store = useUserDetailsStore.getState();
      if (store.userDetails || store.lastUsername) {
        store.clear();
      }
      return;
    }

    const username = auth.user.preferred_username.toUpperCase();
    const store = useUserDetailsStore.getState();

    // ✅ OPTIMIZACIÓN: Solo hacer fetch si NO tenemos datos de este usuario
    // Verificamos usando getState() para evitar dependencias reactivas
    if (store.lastUsername === username && store.userDetails) {
      // Ya tenemos datos válidos de este usuario
      return;
    }

    // ✅ Si ya está cargando, evitar llamada duplicada
    if (store.loading) {
      return;
    }

    // Iniciar fetch
    const fetchUserDetails = async () => {
      store.setLoading(true);
      try {
        const res = await api.get(`/usuario/${username}`);
        useUserDetailsStore.getState().setData(res.data, username);
      } catch (error) {
        console.error('Error fetching user details:', error);
        sentry.captureException(error);
        useUserDetailsStore.getState().setError(error.message);
      }
    };

    fetchUserDetails();
    
  }, [auth.authenticated, auth.user?.preferred_username, api]);
  // ✅ CRÍTICO: Solo dependencias que indican cambio de usuario
  // NO incluir loading ni userDetails para evitar re-ejecuciones

  return {
    // Spread todas las propiedades originales de useAuth
    ...auth,
    
    // Agregar userDetails del backend
    userDetails,
    loadingDetails: loading,
    
    // Propiedades computadas para conveniencia
    username: auth.user?.preferred_username?.toUpperCase(),
    email: auth.user?.email,
    fullName: auth.user?.name ||
              (auth.user?.given_name && auth.user?.family_name
                ? `${auth.user.given_name} ${auth.user.family_name}`
                : auth.user?.preferred_username),
    
    // Helpers adicionales de roles
    /**
     * Verifica si tiene el rol de acceso a Compras
     * @returns {boolean}
     */
    hasComprasAccess: auth.hasRealmRole?.('acceso-compras') ?? false,
    
    /**
     * Verifica si es desarrollador de sistemas
     * @returns {boolean}
     */
    isDeveloper: auth.hasRealmRole?.('S_Desarrollo_de_sistemas') ?? false,
  };
};

export default useComprasAuth;
